import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { pageWithPayloadAndQuery } from '../../../../redux/actions/Page';
import { orgIdSelector } from '../../../../selectors/orgIdSelector';
import { ErrorBanner } from '../../../Components/ErrorBanner';
import ActionsBar from './ActionsBar';
import RefreshButton from './RefreshButton';
import ResetButton from './ResetButton';
import ResourceTitle from './ResourceTitle';
import Pagination from './Pagination';

import './TableContainerLarge.scss';

/*
Constants and helpers
*/

const onClickIp = ({ gotoElt, orgId }) => ipId => (evt) => {
  evt.stopPropagation();

  gotoElt({ ipId, orgId });
};

/*
Component
*/

const TableContainerLarge = (props) => {
  const {
    filters,
    data,
    dispatchClearErr,
    dispatchFetchData,
    dispatchPagination,
    dispatchReset,
    dispatchSetFilter,
    dispatchSetSorting,
    errorMessage,
    getRows,
    gotoElt,
    orgId,
    pageNum,
    pageSize,
    sorting = {},
    tableFilters,
    tableQuery,
    Table,
    titleStr,
    totalItems,
    totalPages,
  } = props;

  const [currentSorting, setCurrentSorting] = useState({});

  useEffect(() => {
    setCurrentSorting(sorting);
  }, [sorting]);

  useEffect(() => {
    if (orgId && tableQuery) {
      dispatchFetchData({
        orgId,
        rawQuery: tableQuery,
      });
    }
  }, [
    dispatchFetchData,
    orgId,
    tableQuery,
  ]);

  const onChangePagination = ({ pageNum: num, pageSize: size }) => {
    dispatchPagination({
      orgId,
      pageNum: num,
      pageSize: size,
    });
  };

  const onClickRefresh = () => {
    dispatchFetchData({
      orgId,
      rawQuery: tableQuery,
    });
  };

  const onClickReset = () => {
    dispatchReset({ orgId, pageSize, tableFilters });
  };

  const updateFilter = dispatchSetFilter({ orgId, pageSize });

  const updateSorting = (fieldName) => {
    const newValue = {
      [fieldName]: !currentSorting[fieldName],
    };

    setCurrentSorting(newValue);
    dispatchSetSorting({ data: newValue, orgId, pageSize });
  };

  const rows = getRows({
    data,
    onClickElt: onClickIp({ gotoElt, orgId }),
  });

  return (
    <div className="resources-table-container">
      <ErrorBanner
        message={errorMessage}
        dismiss={dispatchClearErr}
      />

      <div className="content">
        <ActionsBar left={1}>
          <ResourceTitle
            titleStr={titleStr}
          />

          <RefreshButton
            onClick={onClickRefresh}
          />

          <ResetButton
            onClick={onClickReset}
          />
        </ActionsBar>


        <Table
          data={rows}
          filters={filters}
          pageSize={pageSize}
          setFilter={updateFilter}
          setSorting={updateSorting}
          sorting={currentSorting}
        />

        <Pagination
          onChange={onChangePagination}
          pageNum={pageNum}
          pageSize={pageSize}
          totalItems={totalItems}
          totalPages={totalPages}
        />
      </div>

    </div>
  );
};

TableContainerLarge.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  dispatchClearErr: PropTypes.func.isRequired,
  dispatchFetchData: PropTypes.func.isRequired,
  dispatchPagination: PropTypes.func.isRequired,
  dispatchReset: PropTypes.func.isRequired,
  dispatchSetFilter: PropTypes.func.isRequired,
  dispatchSetSorting: PropTypes.func.isRequired,
  errorMessage: PropTypes.string.isRequired,
  filters: PropTypes.shape({}).isRequired,
  getRows: PropTypes.func.isRequired,
  gotoElt: PropTypes.func.isRequired,
  orgId: PropTypes.string.isRequired,
  pageNum: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  sorting: PropTypes.shape({}).isRequired,
  Table: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.func,
    PropTypes.node,
  ]).isRequired,
  tableFilters: PropTypes.arrayOf(PropTypes.string),
  tableQuery: PropTypes.shape({}).isRequired,
  titleStr: PropTypes.string,
  totalItems: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,

};

TableContainerLarge.defaultProps = {
  tableFilters: [],
  titleStr: '',
};

const makeMapStateToProps =
(_, {
  dataSelector,
  errorMessageSelector,
  filtersSelector,
  metaSelector,
  paginationSelector,
  sortingSelector,
  tableQuerySelector = () => {},
}) => {
  const mapStateToProps = (state) => {
    const data = dataSelector(state);
    const errorMessage = errorMessageSelector(state);

    const filters = filtersSelector(state);

    const orgId = orgIdSelector(state);

    const {
      pageNum,
      pageSize,
    } = paginationSelector(state);

    const meta = metaSelector(state);
    const { totalItems, totalPages } = meta;

    const sorting = sortingSelector(state);

    const tableQuery = tableQuerySelector(state);

    return ({
      data,
      errorMessage,
      filters,
      orgId,
      pageNum,
      pageSize,
      sorting,
      tableQuery,
      totalItems,
      totalPages,
    });
  };

  return mapStateToProps;
};

const makeMapDispatchToProps = (_, {
  clearErr = () => () => {},
  fetchData,
  secondLevel,
  setFilter = () => () => {},
  setSorting = () => () => {},
  receivePagination = () => () => {},
}) => {
  const mapDispatchToProps = dispatch => ({

    dispatchClearErr: () => {
      dispatch(clearErr);
    },

    dispatchFetchData: (query) => {
      dispatch(fetchData(query));
    },

    dispatchPagination: ({
      orgId,
      pageNum,
      pageSize,
    }) => {
      dispatch(receivePagination({
        insertId: orgId,
        data: {
          pageNum,
          pageSize,
        },
      }));
    },

    dispatchReset: ({ orgId, pageSize, tableFilters }) => {
      // Reset page number and size
      dispatch(receivePagination({
        insertId: orgId,
        data: {
          pageNum: 1,
          pageSize,
        },
      }));

      // Set sorting to 'desc' by 'lastSeen'
      dispatch(setSorting({
        insertId: orgId,
        data: { lastSeen: false },
      }));

      // Clear all filters
      const filterData = tableFilters
        .reduce((accum, key) => {
          accum[key] = { unset: true };

          return accum;
        }, {});

      dispatch(setFilter({
        insertId: orgId,
        data: filterData,
      }));
    },

    dispatchSetFilter: ({ orgId, pageSize }) => ({ fieldName, filter }) => {
      const data = {
        [fieldName]: { ...filter },
      };

      dispatch(setFilter({ insertId: orgId, data }));

      dispatch(receivePagination({
        insertId: orgId,
        data: {
          pageNum: 1,
          pageSize,
        },
      }));
    },

    dispatchSetSorting: ({ data, orgId, pageSize }) => {
      dispatch(setSorting({ insertId: orgId, data }));

      dispatch(receivePagination({
        insertId: orgId,
        data: {
          pageNum: 1,
          pageSize,
        },
      }));
    },

    gotoElt: ({ ipId, orgId }) => {
      const payload = {
        orgId,
        toplevel: 'investigate',
        secondlevel: secondLevel,
        id1: ipId,
      };

      dispatch(pageWithPayloadAndQuery({
        payload,
        query: {},
      }));
    },
  });

  return mapDispatchToProps;
};

// Default export
export default
connect(makeMapStateToProps, makeMapDispatchToProps)(TableContainerLarge);
